import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { InventoryItem } from "../../inventory/inventory";
import { Address, EnvironmentConfig, Location } from "../../core/core";

declare const environment: EnvironmentConfig;
export interface OpenAIConfig {
  apiKey?: string; // Made optional since we're using proxy
  model?: string;
  maxTokens?: number;
  temperature?: number;
  baseUrl?: string; // This will now point to our Admin API proxy
}

export interface OpenAIResponse {
  content: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  model: string;
}

export interface OpenAIRequest {
  model: string;
  messages: Array<{
    role: "system" | "user" | "assistant";
    content:
      | string
      | Array<{
          type: "text" | "image_url";
          text?: string;
          image_url?: {
            url: string;
          };
        }>;
  }>;
  max_tokens?: number;
  max_completion_tokens?: number;
  temperature?: number;
  reasoning_effort?: string;
  response_format?: { type: "json_object" | "text" };
}

export interface MachineTypeDefinitionOption {
  id: string;
  name: string;
}

export interface MachineAnalysisResult {
  machineTypeDefinitionId?: string;
  machineType: string;
  make: string;
  model: string;
  year: string;
  monthlyRate?: number;
  weeklyRate?: number;
  dailyRate?: number;
  confidence: number;
  explanation: string;
}

export interface AttachmentSuggestion {
  name: string;
  type: string;
  description: string;
  monthlyRate: number;
  weeklyRate: number;
  dailyRate: number;
  width?: string;
  confidence: number;
}

export interface FeatureSuggestion {
  name: string;
  type: string;
  description: string;
  confidence: number;
}

export interface OptionSuggestion {
  name: string;
  description: string;
  confidence: number;
}

export interface AttachmentAndFeatureSuggestionsResult {
  attachments: AttachmentSuggestion[];
  features: FeatureSuggestion[];
  confidence: number;
}

export interface LocationBasedRentalRatesResult {
  monthlyRate: number;
  weeklyRate: number;
  dailyRate: number;
  marketAnalysis: string;
  potentialSaleValue: number;
  confidence: number;
}

/** Full street address for transport estimates. Never the location display name. */
export function formatTransportationAddress(
  location: {
    address?: Address;
    name?: string;
    formattedAddress?: string;
    latitude?: string | number;
    longitude?: string | number;
    point?: { coordinates?: number[] };
  } | null | undefined,
  fallback = "Unknown location",
): string {
  if (!location) {
    return fallback;
  }

  const street = location.address?.address1?.trim();
  if (street) {
    const formatted = new Address().toString(location.address);
    if (formatted?.trim()) {
      return formatted.trim();
    }
  }

  const indexedAddress = location.formattedAddress?.trim();
  const displayName = location.name?.trim();
  if (indexedAddress && indexedAddress !== displayName) {
    return indexedAddress;
  }

  let lat = location.latitude;
  let lng = location.longitude;
  const coords = location.point?.coordinates;
  if ((lat == null || lng == null) && Array.isArray(coords) && coords.length >= 2) {
    lng = coords[0];
    lat = coords[1];
  }
  const latN = Number(lat);
  const lngN = Number(lng);
  if (Number.isFinite(latN) && Number.isFinite(lngN) && !(latN === 0 && lngN === 0)) {
    return `${latN}, ${lngN}`;
  }

  return fallback;
}

/** Mapbox driving route constrained to commercial vehicle size. Height and width are meters. */
const COMMERCIAL_ROUTE_MAX_HEIGHT_METERS = "4.11";
const COMMERCIAL_ROUTE_MAX_WIDTH_METERS = "3";
const METERS_PER_MILE = 1609.344;
const LBS_PER_METRIC_TON = 2204.6226218;

/** Machine weight in metric tons for Mapbox max_weight. Unknown weight uses a heavy-haul default. */
export function commercialRouteWeightTons(weightLbs: unknown): number {
  const lbs = Number(weightLbs);
  const tons = Number.isFinite(lbs) && lbs > 0 ? lbs / LBS_PER_METRIC_TON : 20;
  const clamped = Math.min(100, Math.max(2.5, tons));
  return Math.round(clamped * 10) / 10;
}

export function transportationCoordinates(
  location: {
    latitude?: string | number;
    longitude?: string | number;
    point?: { coordinates?: number[] };
  } | null | undefined,
): { longitude: number; latitude: number } | null {
  if (!location) {
    return null;
  }

  let lat = location.latitude;
  let lng = location.longitude;
  const coords = location.point?.coordinates;
  if ((lat == null || lng == null) && Array.isArray(coords) && coords.length >= 2) {
    lng = coords[0];
    lat = coords[1];
  }
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || (latitude === 0 && longitude === 0)) {
    return null;
  }
  return { longitude, latitude };
}

function commercialDistancePrompt(roundTripMiles: number | null): string {
  if (roundTripMiles == null) {
    return "";
  }
  const oneWayMiles = Math.round(roundTripMiles / 2);
  return `
Commercial truck route from Mapbox (roads that allow this vehicle height, width, and weight, not a car route):
- One-way: ${oneWayMiles} miles
- Round trip: ${roundTripMiles} miles
Use this round-trip distance. Do not estimate a different mileage.
`;
}

@Injectable({
  providedIn: "root",
})
export class OpenAIService {
  private config: OpenAIConfig = {
    // Remove API key since it's now handled by the backend
    model: "gpt-4.1",
    maxTokens: 1000,
    temperature: 0.7,
    // Update base URL to point to our Admin API proxy
    baseUrl: `${environment.adminApiDomain}/api/admin/v1/openai`,
  };

  constructor(private http: HttpClient) {}

  /**
   * Configure the OpenAI service with optional settings
   * Note: API key is no longer needed as it's handled by the backend
   */
  configure(config: Partial<OpenAIConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Send a prompt to OpenAI and get a response
   */
  sendPrompt(prompt: string, systemMessage?: string): Observable<OpenAIResponse> {
    const messages = [];

    if (systemMessage) {
      messages.push({
        role: "system" as const,
        content: systemMessage,
      });
    }

    messages.push({
      role: "user" as const,
      content: prompt,
    });

    const requestBody: OpenAIRequest = {
      model: this.config.model!,
      messages,
      max_tokens: this.config.maxTokens,
      temperature: this.config.temperature,
    };

    // Use admin API proxy instead of calling OpenAI directly
    const adminApiUrl = `${environment.adminApiDomain}/api/admin/v1/openai/chat/completions`;
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http
      .post<any>(adminApiUrl, requestBody, { headers: headers })
      .pipe(
        map(response => this.parseResponse(response)),
        catchError(error => {
          console.error("OpenAI API Error:", error);
          return throwError(() => new Error(`OpenAI API Error: ${error.error?.error?.message || error.message || "Unknown error"}`));
        }),
      );
  }

  /**
   * Send a simple prompt without system message
   */
  prompt(prompt: string): Observable<string> {
    return this.sendPrompt(prompt).pipe(map(response => response.content));
  }

  /**
   * Send a conversation with multiple messages
   */
  sendConversation(messages: Array<{ role: "user" | "assistant"; content: string }>, systemMessage?: string): Observable<OpenAIResponse> {
    const conversationMessages = [];

    if (systemMessage) {
      conversationMessages.push({
        role: "system" as const,
        content: systemMessage,
      });
    }

    conversationMessages.push(
      ...messages.map(msg => ({
        role: msg.role as "user" | "assistant",
        content: msg.content,
      })),
    );

    const requestBody: OpenAIRequest = {
      model: this.config.model!,
      messages: conversationMessages,
      max_tokens: this.config.maxTokens,
      temperature: this.config.temperature,
    };

    // Use admin API proxy instead of calling OpenAI directly
    const adminApiUrl = `${environment.adminApiDomain}/api/admin/v1/openai/chat/completions`;
    // No need for Authorization header since the backend handles it
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http
      .post<any>(adminApiUrl, requestBody, { headers: headers })
      .pipe(
        map(response => this.parseResponse(response)),
        catchError(error => {
          console.error("OpenAI API Error:", error);
          return throwError(() => new Error(`OpenAI API Error: ${error.error?.error?.message || error.message || "Unknown error"}`));
        }),
      );
  }

  /**
   * Parse the OpenAI API response
   */
  private parseResponse(response: any): OpenAIResponse {
    const choice = response.choices?.[0];
    if (!choice) {
      throw new Error("No response from OpenAI API");
    }

    return {
      content: choice.message?.content || "",
      usage: response.usage
        ? {
            promptTokens: response.usage.prompt_tokens,
            completionTokens: response.usage.completion_tokens,
            totalTokens: response.usage.total_tokens,
          }
        : undefined,
      model: response.model,
    };
  }

  /**
   * Get the current configuration
   */
  getConfig(): OpenAIConfig {
    return { ...this.config };
  }

  /**
   * Check if the service is properly configured
   * Note: API key check is no longer needed since backend handles authentication
   */
  isConfigured(): boolean {
    return true; // Always true since backend handles configuration
  }

  /**
   * Resolves catalog type definition id from model output (prefers explicit id, then name match).
   */
  private resolveMachineTypeDefinitionId(
    parsed: { machineTypeDefinitionId?: string; machineType?: string },
    options: MachineTypeDefinitionOption[],
  ): string | undefined {
    if (!options.length) {
      return undefined;
    }
    const rawId = (parsed.machineTypeDefinitionId || "").trim();
    if (rawId && options.some(o => o.id === rawId)) {
      return rawId;
    }
    const nt = (parsed.machineType || "").trim().toLowerCase();
    if (!nt || nt === "unknown") {
      return undefined;
    }
    const exact = options.find(o => o.name.toLowerCase() === nt);
    if (exact) {
      return exact.id;
    }
    if (nt.length < 2) {
      return undefined;
    }
    const partial = options.filter(o => o.name.toLowerCase().includes(nt));
    if (!partial.length) {
      return undefined;
    }
    partial.sort((a, b) => a.name.length - b.name.length);
    return partial[0].id;
  }

  /**
   * Analyze a machine image to identify type, make, model, year and suggest rental rates.
   * Pass `availableMachineTypes` with real type definition ids from the type-definitions index so the model can return `machineTypeDefinitionId` for catalog alignment.
   */
  analyzeMachineImage(imageFile: File, availableMachineTypes?: MachineTypeDefinitionOption[]): Observable<MachineAnalysisResult | null> {
    return new Observable(observer => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;

        const typeOptions = availableMachineTypes?.length ? availableMachineTypes : [];
        const typesJson = JSON.stringify(typeOptions);

        const legacyNameList =
          "mini excavator, skid steer, dozer, wheel loader, excavator, backhoe, crane, forklift, compactor, grader";

        const systemMessage =
          typeOptions.length > 0
            ? `You are an expert in construction equipment identification. Analyze the provided image and identify the machine's type, make, model, and year. Also suggest appropriate rental rates based on current market conditions and provide a high-level description of the machine's capabilities.

AVAILABLE_MACHINE_TYPES (JSON array). You MUST choose exactly one entry; copy its "id" and "name" fields into your response — these are Rubbl catalog type definition identifiers:
${typesJson}

IMPORTANT: You must respond in this EXACT JSON format (no additional text, no markdown formatting):
{
  "machineTypeDefinitionId": "exact id string from one object in AVAILABLE_MACHINE_TYPES",
  "machineType": "exact name string from that same object",
  "make": "manufacturer name",
  "model": "specific model",
  "year": "estimated year (YYYY format)",
  "monthlyRate": estimated_monthly_rental_rate_number,
  "weeklyRate": estimated_weekly_rental_rate_number,
  "dailyRate": estimated_daily_rental_rate_number,
  "confidence": confidence_score_0_to_100,
  "explanation": "High-level description of the machine including its typical applications, capabilities, and key features that justify the suggested rental rates"
}

For rental rates, consider:
- Daily rates: typically 10-15% of monthly rate
- Weekly rates: typically 25-35% of monthly rate
- Market conditions, machine size, age, and capabilities

CRITICAL: machineTypeDefinitionId MUST be the "id" field from exactly one object in AVAILABLE_MACHINE_TYPES. machineType MUST be that object's "name" field (same entry).

If you cannot identify the machine clearly, set confidence to a low number and use "Unknown" for unidentifiable fields.`
            : `You are an expert in construction equipment identification. Analyze the provided image and identify the machine's type, make, model, and year. Also suggest appropriate rental rates based on current market conditions and provide a high-level description of the machine's capabilities.

IMPORTANT: You must respond in this EXACT JSON format (no additional text, no markdown formatting):
{
  "machineType": "specific type (MUST use one of these exact names: ${legacyNameList})",
  "make": "manufacturer name",
  "model": "specific model",
  "year": "estimated year (YYYY format)",
  "monthlyRate": estimated_monthly_rental_rate_number,
  "weeklyRate": estimated_weekly_rental_rate_number,
  "dailyRate": estimated_daily_rental_rate_number,
  "confidence": confidence_score_0_to_100,
  "explanation": "High-level description of the machine including its typical applications, capabilities, and key features that justify the suggested rental rates"
}

For rental rates, consider:
- Daily rates: typically 10-15% of monthly rate
- Weekly rates: typically 25-35% of monthly rate
- Market conditions, machine size, age, and capabilities

CRITICAL: The machineType field MUST match exactly one of: ${legacyNameList}

If you cannot identify the machine clearly, set confidence to a low number and use "Unknown" for unidentifiable fields.`;

        const messages = [
          {
            role: "system" as const,
            content: systemMessage,
          },
          {
            role: "user" as const,
            content: [
              {
                type: "text" as const,
                text: "Please analyze this construction machine image and provide the information in the specified JSON format.",
              },
              {
                type: "image_url" as const,
                image_url: {
                  url: base64String,
                },
              },
            ],
          },
        ];

        const requestBody: OpenAIRequest = {
          model: "gpt-4o",
          messages,
          max_tokens: 500,
          temperature: 0.3,
          response_format: { type: "json_object" },
        };

        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.config.apiKey}`,
        });

        // Use the admin API proxy instead of calling OpenAI directly
        const adminApiUrl = `${environment.adminApiDomain}/api/admin/v1/openai/chat/completions`;
        const proxyHeaders = new HttpHeaders({
          "Content-Type": "application/json",
        });

        this.http
          .post<any>(adminApiUrl, requestBody, { headers: proxyHeaders })
          .pipe(
            map(response => {
              const choice = response.choices?.[0];
              if (!choice) {
                return null;
              }

              try {
                const content = choice.message?.content || "";
                // Clean up the response in case there's any markdown formatting
                const cleanedContent = content.replace(/```json\n?|\n?```/g, "").trim();
                const result = JSON.parse(cleanedContent);

                // Validate the response structure
                if (!result.make || !result.model || !result.year) {
                  return null;
                }

                if (typeOptions.length > 0) {
                  const resolvedId = this.resolveMachineTypeDefinitionId(result, typeOptions);
                  if (resolvedId) {
                    const opt = typeOptions.find(o => o.id === resolvedId);
                    result.machineTypeDefinitionId = resolvedId;
                    if (opt) {
                      result.machineType = opt.name;
                    }
                  }
                  if (!result.machineType || String(result.machineType).toLowerCase() === "unknown") {
                    return null;
                  }
                } else {
                  if (!result.machineType) {
                    return null;
                  }
                }

                return result as MachineAnalysisResult;
              } catch (error) {
                console.error("Failed to parse machine analysis response:", error);
                return null;
              }
            }),
            catchError(error => {
              console.error("Machine analysis error:", error);
              return of(null);
            }),
          )
          .subscribe({
            next: result => {
              observer.next(result);
              observer.complete();
            },
            error: err => {
              observer.error(err);
            },
          });
      };

      reader.onerror = () => {
        observer.error(new Error("Failed to read image file"));
      };

      reader.readAsDataURL(imageFile);
    });
  }

  /**
   * Estimate transportation cost using the machine origin address and the renter destination.
   * Destination may be a full location (marketplace) or a ZIP string (Admin generate-quote).
   */
  estimateTransportationCost(
    machine: InventoryItem,
    destination: Location | string,
  ): Observable<{ min: number; max: number; average: number; distance: number; explanation: string } | null> {
    const machineLocationInfo = formatTransportationAddress(machine?.location, "Unknown machine location");
    const destinationInfo =
      typeof destination === "string"
        ? destination
        : formatTransportationAddress(destination, destination?.address?.postalCode || "Unknown delivery address");

    return this.commercialRoundTripMiles(machine, destination).pipe(
      switchMap(roundTripMiles => {
        const prompt = `Estimate the round-trip transportation cost for a construction equipment rental from the machine's address to the renter's delivery address.

Machine (origin) address: ${machineLocationInfo}
Renter (destination) address: ${destinationInfo}

Machine details:
- Weight: ${machine.weight || "Unknown"} lbs
${commercialDistancePrompt(roundTripMiles)}
Please provide a cost range for the round-trip transportation cost in USD. Use local market pricing for this route, not a national average. Consider factors like:
- Distance between the two addresses
- Weight of the machine
- Local hauling rates
- Local fuel/diesel cost

Make sure the minimum and maximum cost range is not too wide.

Return the response in this exact format:
First line: four numbers separated by commas (minimum cost, maximum cost, average cost, estimated distance in miles)
Second line: 1-2 sentence explanation of how you calculated the estimate. Do not include any city names or state names.

Example:
2000,3500,2750,150
Based on current local diesel prices, machine weight, and local hauling rates for the 150-mile round trip, the estimate includes fuel, the 25,000 lb weight, labor, permits, and specialized transport equipment.`;

        const requestBody: OpenAIRequest = {
          model: "gpt-5.6-sol",
          messages: [
            {
              role: "user",
              content: prompt,
            },
          ],
          reasoning_effort: "none",
          temperature: 0,
          max_completion_tokens: 1000,
        };

        const adminApiUrl = `${environment.adminApiDomain}/api/admin/v1/openai/chat/completions`;
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
        });

        return this.http.post<any>(adminApiUrl, requestBody, { headers }).pipe(
          map(response => {
            const parsed = this.parseResponse(response);
            const lines = parsed.content.trim().split("\n");
            if (lines.length < 2) {
              return null;
            }

            const costMatches = lines[0].match(/\d+/g);
            if (costMatches && costMatches.length >= 4) {
              const min = parseInt(costMatches[0], 10);
              const max = parseInt(costMatches[1], 10);
              const average = parseInt(costMatches[2], 10);
              const distance = roundTripMiles ?? parseInt(costMatches[3], 10);
              const explanation = lines[1].trim();
              return { min, max, average, distance, explanation };
            }
            return null;
          }),
          catchError(error => {
            console.error("Failed to estimate transportation cost:", error);
            return of(null);
          }),
        );
      }),
    );
  }

  /** One-way Mapbox driving miles, doubled for the round trip. Null when coordinates or the route are missing. */
  private commercialRoundTripMiles(machine: InventoryItem, destination: Location | string): Observable<number | null> {
    const origin = transportationCoordinates(machine?.location);
    if (!origin || !environment?.mapboxKey) {
      return of(null);
    }

    return this.destinationCoordinates(destination).pipe(
      switchMap(dest => {
        if (!dest) {
          return of(null);
        }

        const path = `${origin.longitude},${origin.latitude};${dest.longitude},${dest.latitude}`;
        const params = new HttpParams()
          .set("access_token", environment.mapboxKey)
          .set("overview", "false")
          .set("steps", "false")
          .set("max_height", COMMERCIAL_ROUTE_MAX_HEIGHT_METERS)
          .set("max_width", COMMERCIAL_ROUTE_MAX_WIDTH_METERS)
          .set("max_weight", String(commercialRouteWeightTons(machine?.weight)));

        return this.http.get<any>(`https://api.mapbox.com/directions/v5/mapbox/driving/${path}`, { params }).pipe(
          map(body => {
            const meters = Number(body?.routes?.[0]?.distance);
            if (!Number.isFinite(meters) || meters <= 0) {
              return null;
            }
            const oneWayMiles = Math.round(meters / METERS_PER_MILE);
            return oneWayMiles > 0 ? oneWayMiles * 2 : null;
          }),
          catchError(() => of(null)),
        );
      }),
    );
  }

  private destinationCoordinates(destination: Location | string): Observable<{ longitude: number; latitude: number } | null> {
    if (typeof destination !== "string") {
      return of(transportationCoordinates(destination));
    }

    const query = destination.trim();
    if (!query || !environment?.mapboxKey) {
      return of(null);
    }

    const params = new HttpParams().set("access_token", environment.mapboxKey).set("limit", "1");
    return this.http
      .get<any>(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`, { params })
      .pipe(
        map(body => {
          const center = body?.features?.[0]?.center;
          if (!Array.isArray(center) || center.length < 2) {
            return null;
          }
          const longitude = Number(center[0]);
          const latitude = Number(center[1]);
          if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            return null;
          }
          return { longitude, latitude };
        }),
        catchError(() => of(null)),
      );
  }

  /**
   * Get attachment suggestions for a specific machine
   * @param make - Machine make (e.g., "Caterpillar")
   * @param model - Machine model (e.g., "320")
   * @param year - Machine year (e.g., "2020")
   * @param machineType - Machine type (e.g., "excavator")
   * @returns Observable containing attachment suggestions
   */
  getAttachmentSuggestions(
    make: string,
    model: string,
    year: string,
    machineType: string,
    availableTypeNames: string[] = [],
  ): Observable<AttachmentSuggestion[] | null> {
    const typesList = availableTypeNames.length > 0
      ? `\n\nAVAILABLE ATTACHMENT TYPES (you MUST pick from this list):\n${availableTypeNames.map(n => `- "${n}"`).join("\n")}\n\nIMPORTANT: The "type" field in your response MUST be one of the names above, copied exactly (case-sensitive). Do NOT invent new type names.`
      : "";

    const systemMessage = `You are an expert in construction equipment attachments and rental pricing. Based on the provided machine details, suggest the most common and compatible attachments with rental rates.

CRITICAL: Respond with ONLY valid JSON array. Do NOT use markdown code blocks. Do NOT include \`\`\`json or \`\`\`. Start your response directly with the opening bracket [ and end with the closing bracket ].
[
  {
    "name": "attachment display name",
    "type": "exact type name from the available types list",
    "description": "brief description of the attachment",
    "monthlyRate": monthly_rental_rate_number,
    "weeklyRate": weekly_rental_rate_number,
    "dailyRate": daily_rental_rate_number,
    "confidence": confidence_score_0_to_100
  }
]${typesList}

For rental rates, consider:
- Daily rates: typically 10-15% of monthly rate
- Weekly rates: typically 25-35% of monthly rate
- Market conditions and compatibility
- Do not include size specifications - users will add sizes themselves

MACHINE TYPE ATTACHMENT REFERENCE:

Skid Steer Loader: Arguably the most versatile machine for its size. Its arms and universal quick-attach plate are designed for rapidly changing attachments.
Most common: General Purpose Bucket
Other popular: Pallet Forks, Auger, Hydraulic Breaker (Hammer), Grapple Bucket (Root or Brush Grapple), Mulcher/Brush Cutter, Trencher, Broom

Mini Excavator: Perfect for digging in tight spaces, utility work, and landscaping. The 'bucket' is key, but the 'thumb' is what makes it truly versatile.
Most common: General Purpose Bucket
Other popular: Hydraulic Thumb (often used in combination with a bucket), Grading/Ditching Bucket (smooth-lipped), Hydraulic Breaker, Auger, Ripper

Backhoe Loader: The classic two-in-one machine. It's a loader on the front and an excavator (hoe) on the back.
Front loader most common: General Purpose Bucket
Front loader other popular: Pallet Forks, Snow Pusher
Back hoe most common: Digging Bucket
Back hoe other popular: Hydraulic Breaker, Auger, Hydraulic Thumb

Excavator (Standard/Full-Size): The heavy-duty workhorse for bulk earthmoving, demolition, and large-scale utility work.
Most common: General Purpose Bucket
Other popular: Hydraulic Hammer (Breaker), Hydraulic Thumb, Shear (for demolition), Grapple (Sorting/Demo), Compaction Plate

Wheel Loader (Front Loader): Designed primarily to move large amounts of material (like gravel, dirt, or snow) and load trucks quickly.
Most common: General Purpose Bucket
Other popular: Pallet Forks (often very large), Snow Pusher, Grapple, Broom

Bulldozer (Dozer): A specialized machine for pushing and grading large quantities of soil, rock, or debris. Its 'attachments' are generally integrated.
Front attachment: Blade (Types: Straight 'S', Universal 'U', Semi-U 'S-U')
Rear attachment: Ripper (single or multi-shank), Winch

Telehandler: Essentially a forklift that can 'reach' forward and upward, making it invaluable for lifting materials onto roofs or upper floors.
Most common: Pallet Forks
Other popular: Truss Jib, Work Platform (Man Basket), Bucket

Compact / Utility Tractor: The jack-of-all-trades for landscaping, property maintenance, and light-duty construction.
Front loader most common: Loader Bucket
Front loader other popular: Pallet Forks, Grapple
Rear 3-point hitch most common: Box Blade
Rear 3-point hitch other popular: Rotary Cutter (Brush Hog), Landscape Rake, Tiller, Auger

Motor Grader: A specialized machine used almost exclusively for creating a flat, level surface (a 'grade'), most commonly for road building and maintenance.
Primary tool: Moldboard (the large blade between the front and rear axles)
Other popular: Ripper/Scarifier, Dozer Blade (front-mounted)

Focus on 16-18 of the most common attachments that would be available for this specific machine type, make. Only suggest attachments that are physically compatible with the machine's hydraulic flow rate, pressure specifications, mounting system (ISO, John Deere, Caterpillar, etc.), and operating weight class. Keep descriptions concise.`;

    const userMessage = `Machine Details:
- Make: ${make}
- Type: ${machineType}

IMPORTANT: Only suggest attachments that are specifically compatible with this exact ${make} ${machineType}. Do not include generic attachments that would work on other machine types. Focus on attachments that would commonly be used with this specific machine model and are physically compatible with its hydraulic specifications, mounting systems, and operating characteristics.

Respond with valid JSON array only (no markdown, no code blocks). Include 16-18 attachments with rental rates for this specific machine.`;

    const messages = [
      {
        role: "system" as const,
        content: systemMessage,
      },
      {
        role: "user" as const,
        content: userMessage,
      },
    ];

    const requestBody: OpenAIRequest = {
      model: "gpt-4o",
      messages,
      max_tokens: 2500,
      temperature: 0.2,
    };

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.config.apiKey}`,
    });

    // Use the admin API proxy
    const adminApiUrl = `${environment.adminApiDomain}/api/admin/v1/openai/chat/completions`;

    return this.http
      .post<any>(adminApiUrl, requestBody, { headers })
      .pipe(
        map(response => {
          const choice = response.choices?.[0];
          if (!choice) {
            return null;
          }

          try {
            const content = choice.message?.content || "";
            console.log("Raw AI response content:", content);

            // Clean up any markdown formatting that might slip through
            const cleanedContent = content.replace(/```json\n?|\n?```/g, "").trim();

            console.log("Cleaned content for parsing:", cleanedContent);

            const result = JSON.parse(cleanedContent);

            // Validate the response structure - expecting an array of attachments
            if (Array.isArray(result)) {
              console.log("Successfully parsed attachment suggestions:", {
                attachmentsCount: result.length,
              });
              return result as AttachmentSuggestion[];
            }

            console.warn("Invalid response structure - expected array:", result);
            return null;
          } catch (error) {
            console.error("Failed to parse attachment suggestions response:", error);
            console.error("Raw response that failed to parse:", choice.message?.content);
            return null;
          }
        }),
        catchError(error => {
          console.error("Failed to get attachment suggestions:", error);
          return of(null);
        }),
      );
  }

  /**
   * Get location-based rental rates and potential sale value
   * @param make - Machine make
   * @param model - Machine model
   * @param year - Machine year
   * @param usageValue - Hour-meter reading OR odometer miles (see isMileageBased)
   * @param location - Location string (city, state, zip)
   * @param isMileageBased - When true, usageValue is odometer miles (not engine hours)
   * @returns Observable containing location-based pricing
   */
  getLocationBasedRentalRates(
    make: string,
    model: string,
    year: string,
    usageValue: string,
    location: string,
    isMileageBased: boolean = false,
  ): Observable<LocationBasedRentalRatesResult | null> {
    const usageFactorLine = isMileageBased
      ? `- Machine condition based on BOTH age (year) AND odometer mileage. The usage number is MILES on the odometer, NOT engine hours. Typical road trucks and similar equipment often have tens of thousands of miles; that is normal and must NOT be interpreted as "engine hours" (which would be absurd if applied to the same numeric value).`
      : `- Machine condition based on BOTH age (year) AND operating hours (older machines and higher hours = lower value and potentially lower rental rates)`;

    const saleValueUsageLine = isMileageBased
      ? `- Sale value: consider depreciation based on age, condition based on odometer miles, and local market demand`
      : `- Sale value: consider depreciation based on age, condition based on hours, and local market demand`;

    const systemMessage = `You are an expert in construction equipment rental pricing and market analysis. Based on the provided machine details and location, provide accurate rental rates and potential sale value considering local market conditions.

IMPORTANT: You must respond in this EXACT JSON format (no additional text, no markdown formatting):
{
  "monthlyRate": monthly_rental_rate_number,
  "weeklyRate": weekly_rental_rate_number,
  "dailyRate": daily_rental_rate_number,
  "marketAnalysis": "brief analysis of local market conditions",
  "potentialSaleValue": estimated_sale_value_number,
  "confidence": confidence_score_0_to_100
}

Consider these factors for pricing:
- Local market demand and competition
- Regional economic conditions
- Seasonal variations in the area
- Equipment availability in the region
${usageFactorLine}
- Daily rates: typically 10-15% of monthly rate
- Weekly rates: typically 25-35% of monthly rate
${saleValueUsageLine}`;

    const usageBullet = isMileageBased ? `Odometer (miles)` : `Hours (meter)`;

    const userMessageCritical = isMileageBased
      ? `CRITICAL: Consider the local market conditions in this specific area AND the machine condition based on BOTH the year (age) and ODOMETER MILES for accurate pricing:
- The usage figure provided is miles on the odometer, not engine hours—do not treat it as hour-meter hours.
- Older machines typically have lower values regardless of mileage
- Higher mileage indicates more wear for road-going use
- Combine age and mileage to determine overall condition and pricing`
      : `CRITICAL: Consider the local market conditions in this specific area AND the machine condition based on BOTH the year (age) and operating hours for accurate pricing. Both age and usage are important factors:
- Older machines (earlier years) typically have lower values regardless of hours
- Higher hours indicate more wear and tear
- A newer machine with very high hours may be worth less than an older machine with low hours
- Combine both factors to determine overall condition and pricing`;

    const userMessage = `Please provide location-based rental rates and potential sale value for:
- Make: ${make}
- Model: ${model}
- Year: ${year}
- ${usageBullet}: ${usageValue}
- Location: ${location}

${userMessageCritical}`;

    const messages = [
      {
        role: "system" as const,
        content: systemMessage,
      },
      {
        role: "user" as const,
        content: userMessage,
      },
    ];

    const requestBody: OpenAIRequest = {
      model: "gpt-4o",
      messages,
      max_tokens: 600,
      temperature: 0.3,
    };

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.config.apiKey}`,
    });

    const adminApiUrl = `${environment.adminApiDomain}/api/admin/v1/openai/chat/completions`;

    return this.http
      .post<any>(adminApiUrl, requestBody, { headers })
      .pipe(
        map(response => {
          const choice = response.choices?.[0];
          if (!choice) {
            return null;
          }

          try {
            const content = choice.message?.content || "";
            const cleanedContent = content.replace(/```json\n?|\n?```/g, "").trim();
            const result = JSON.parse(cleanedContent);

            // Validate the response structure
            if (result.monthlyRate && result.weeklyRate && result.dailyRate) {
              return result as LocationBasedRentalRatesResult;
            }
            return null;
          } catch (error) {
            console.error("Failed to parse location-based rental rates response:", error);
            return null;
          }
        }),
        catchError(error => {
          console.error("Failed to get location-based rental rates:", error);
          return of(null);
        }),
      );
  }

  /**
   * Get option suggestions for a machine
   * @param make - Machine make
   * @param model - Machine model
   * @param year - Machine year
   * @param machineType - Machine type
   * @returns Observable containing option suggestions
   */
  getOptionSuggestions(make: string, model: string, year: string, machineType: string): Observable<OptionSuggestion[] | null> {
    const systemMessage = `You are an expert in construction equipment options and features. Based on the provided machine details, suggest the most common options and features that this machine might have.

CRITICAL: Respond with ONLY valid JSON array. Do NOT use markdown code blocks. Do NOT include \`\`\`json or \`\`\`. Start your response directly with the opening bracket [ and end with the closing bracket ].
[
  {
    "name": "option display name",
    "description": "brief description of the option",
    "confidence": confidence_score_0_to_100
  }
]

Common machine options include:
- Air Conditioning
- High Flow Hydraulics (very common)
- Joystick Controls
- Cab Enclosure
- Heater
- Radio/Bluetooth Audio
- Backup Camera
- Rearview Camera
- 360-Degree Camera System
- LED Work Lights
- Halogen Work Lights
- Quick Coupler/Quick Attach
- Pilot Controls
- Pattern Change Valve
- Blade Float
- Ripper
- Air Ride Seat
- Heated Seat
- Adjustable Armrests
- Suspension Seat
- Auxiliary Hydraulics
- Third Auxiliary Hydraulics
- Two-Speed Travel
- Automatic Greasing System
- Theft Deterrent System
- Telematics/GPS Tracking
- Central Lubrication System
- Forestry Package
- Cold Weather Package
- Rubber Tracks (instead of steel)
- Steel Tracks
- Road Pads
- Fenders
- Counterweight

Focus on up to 20 of the most common and relevant options for this specific machine type, make, model, and year. Keep descriptions concise.`;

    const userMessage = `Machine Details:
- Make: ${make}
- Model: ${model}
- Year: ${year}
- Type: ${machineType}

Please suggest the most common and valuable options for this machine.`;

    const messages = [
      {
        role: "system" as const,
        content: systemMessage,
      },
      {
        role: "user" as const,
        content: userMessage,
      },
    ];

    const requestBody: OpenAIRequest = {
      model: "gpt-4o",
      messages,
      max_tokens: 2500,
      temperature: 0.5,
    };

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.config.apiKey}`,
    });

    const adminApiUrl = `${environment.adminApiDomain}/api/admin/v1/openai/chat/completions`;

    return this.http
      .post<any>(adminApiUrl, requestBody, { headers })
      .pipe(
        map(response => {
          const choice = response.choices?.[0];
          if (!choice) {
            return null;
          }

          try {
            const content = choice.message?.content || "";
            console.log("Raw OpenAI response for options:", content);

            // Clean up the response - remove markdown code blocks if present
            let cleanedContent = content.trim();
            cleanedContent = cleanedContent.replace(/```json\n?|\n?```/g, "");
            cleanedContent = cleanedContent.trim();

            console.log("Cleaned content for parsing:", cleanedContent);

            const result = JSON.parse(cleanedContent);

            // Validate the response structure
            if (Array.isArray(result)) {
              console.log("Successfully parsed option suggestions:", {
                optionsCount: result.length,
              });
              return result as OptionSuggestion[];
            }

            console.warn("Invalid response structure - expected array:", result);
            return null;
          } catch (error) {
            console.error("Failed to parse option suggestions response:", error);
            console.error("Raw response that failed to parse:", choice.message?.content);
            return null;
          }
        }),
        catchError(error => {
          console.error("Failed to get option suggestions:", error);
          return of(null);
        }),
      );
  }

  /**
   * Generate a machine description based on machine details, attachments, and options
   * @param make - Machine make
   * @param model - Machine model
   * @param year - Machine year
   * @param machineType - Machine type
   * @param hours - Machine hours
   * @param attachments - Selected attachments
   * @param options - Selected options
   * @returns Observable containing generated description
   */
  generateMachineDescription(
    make: string,
    model: string,
    year: string,
    machineType: string,
    hours: number,
    attachments: string[],
    options: string[],
  ): Observable<string | null> {
    const systemMessage = `You are an expert in construction equipment and creating compelling, professional machine descriptions for rental listings. Based on the provided machine details, generate a concise, professional description that highlights key features and benefits.

The description should:
- Be 2-4 sentences long
- Highlight the machine type, make, model, and year
- Mention key options and attachments if provided
- Focus on capabilities and benefits for potential renters
- Use professional, confident language
- NOT include pricing information
- NOT include location information

Respond with ONLY the description text, no JSON, no markdown, no additional formatting.`;

    const attachmentsList = attachments.length > 0 ? attachments.join(", ") : "none";
    const optionsList = options.length > 0 ? options.join(", ") : "none";

    const userMessage = `Generate a professional rental listing description for:

Machine Details:
- Type: ${machineType}
- Make: ${make}
- Model: ${model}
- Year: ${year}
- Hours: ${hours}

Attachments included: ${attachmentsList}

Options/Features: ${optionsList}

Generate a compelling 2-4 sentence description that would attract renters to this machine.`;

    const messages = [
      {
        role: "system" as const,
        content: systemMessage,
      },
      {
        role: "user" as const,
        content: userMessage,
      },
    ];

    const requestBody: OpenAIRequest = {
      model: "gpt-4o",
      messages,
      max_tokens: 300,
      temperature: 0.7,
    };

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.config.apiKey}`,
    });

    const adminApiUrl = `${environment.adminApiDomain}/api/admin/v1/openai/chat/completions`;

    return this.http
      .post<any>(adminApiUrl, requestBody, { headers })
      .pipe(
        map(response => {
          const choice = response.choices?.[0];
          if (!choice) {
            return null;
          }

          const description = choice.message?.content?.trim() || null;
          console.log("Generated machine description:", description);
          return description;
        }),
        catchError(error => {
          console.error("Failed to generate machine description:", error);
          return of(null);
        }),
      );
  }

  /**
   * Get machine specifications from OpenAI based on make and model
   * @param make - Machine make
   * @param model - Machine model
   * @returns Observable containing parsed specifications array grouped by category
   */
  getMachineSpecifications(make: string, model: string): Observable<any[] | null> {
    const systemMessage = `You are an expert in construction equipment specifications. Based on the machine make and model provided, generate a comprehensive list of technical specifications grouped by category.

Return specifications in the following JSON format as an array of category objects. Each category has a name and an items array containing specification items.

Example format:
[
  {
    "name": "Engine",
    "items": [
      {
        "name": "Horsepower",
        "value": "310hp",
        "displayOnCard": false
      },
      {
        "name": "Engine Type",
        "value": "Diesel",
        "displayOnCard": false
      },
      {
        "name": "Displacement",
        "value": "928cid",
        "displayOnCard": false
      }
    ]
  },
  {
    "name": "Weights & Dimensions",
    "items": [
      {
        "name": "Total Operating Weight",
        "value": "75845lbs",
        "displayOnCard": false
      },
      {
        "name": "Overall Length w/Dozer",
        "value": "240in",
        "displayOnCard": false
      }
    ]
  },
  {
    "name": "Hydraulics",
    "items": [
      {
        "name": "Hydraulic Pump Flow",
        "value": "73gal/min",
        "displayOnCard": false
      }
    ]
  }
]

Common categories include:
- Engine (horsepower, engine type, displacement, etc.)
- Weights & Dimensions (operating weight, length, width, height, ground clearance)
- Hydraulics (pump flow, relief pressure, pump type)
- Transmission (speeds, travel speed, transmission type)
- Undercarriage (track gauge, track width, number of rollers) - for tracked equipment
- Operator Environment (cab type, protection level)
- Fluid Capacities (fuel tank, hydraulic fluid)
- Power & Controls (battery voltage, electrical system)
- And category-specific specs (Dozer for dozers, Bucket for excavators/loaders, etc.)

For values, include the unit in the value string (e.g., "310hp", "75845lbs", "240in").
Set displayOnCard to false for all items.

Respond with ONLY valid JSON array, no markdown formatting.`;

    const userMessage = `Generate technical specifications for:
Make: ${make}
Model: ${model}`;

    const messages = [
      {
        role: "system" as const,
        content: systemMessage,
      },
      {
        role: "user" as const,
        content: userMessage,
      },
    ];

    const requestBody: OpenAIRequest = {
      model: "gpt-4o",
      messages,
      max_tokens: 2000,
      temperature: 0.3, // Lower temperature for more consistent formatting
    };

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.config.apiKey}`,
    });

    const adminApiUrl = `${environment.adminApiDomain}/api/admin/v1/openai/chat/completions`;

    return this.http
      .post<any>(adminApiUrl, requestBody, { headers })
      .pipe(
        map(response => {
          const choice = response.choices?.[0];
          if (!choice) {
            return null;
          }

          try {
            const content = choice.message?.content || "";
            console.log("Raw OpenAI response for specifications:", content);

            // Clean up the response - remove markdown code blocks if present
            let cleanedContent = content.trim();
            cleanedContent = cleanedContent.replace(/```json\n?|\n?```/g, "");
            cleanedContent = cleanedContent.trim();

            console.log("Cleaned content for parsing:", cleanedContent);

            const result = JSON.parse(cleanedContent);

            // Validate the response structure
            if (Array.isArray(result)) {
              console.log("Successfully parsed specifications:", {
                specsCount: result.length,
              });
              return result;
            }

            console.warn("Invalid response structure - expected array:", result);
            return null;
          } catch (error) {
            console.error("Failed to parse specifications response:", error);
            console.error("Raw response that failed to parse:", choice.message?.content);
            return null;
          }
        }),
        catchError(error => {
          console.error("Failed to get machine specifications:", error);
          return of(null);
        }),
      );
  }
}
